import { Button, Col, Divider, Form, Input, Modal, Row, Select, Typography } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'

import {
  AccountPublic, BillingPrice, BillingProduct, CountryChoice, Organization, UserPrivate
} from '@pollination-solutions/pollination-sdk'

import { useAuth } from 'auth-context'

// Currency formatting helper
const getCurrencySymbol = (currencyCode: string): string => {
  const symbols: { [key: string]: string } = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£'
  }
  return symbols[currencyCode] || currencyCode
}

const formatCurrency = (amount: string | number, currencyCode: string): string => {
  let numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  numAmount = numAmount / 100 // Convert cents to dollars
  const symbol = getCurrencySymbol(currencyCode)
  const formatted = numAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

  return `${symbol}${formatted}`
}

// Invoice Request Form Data Interface
export interface InvoiceRequestFormData {
  additional_contact_email?: string
  billing_address: string
  city: string
  state: string
  postal_code: string
  country: string
  additional_info?: string
}

// Selected Product Interface (matches the one in NewBillingSubscriptionForm)
interface SelectedProduct {
  product: BillingProduct
  price: BillingPrice
  quantity: number
}

interface InvoiceRequestModalProps {
  visible: boolean
  user: UserPrivate
  account: AccountPublic
  onClose: () => void
  onSubmit: (formData: InvoiceRequestFormData, organization: Organization | null) => Promise<void>
  loading: boolean
  selectedProducts: SelectedProduct[]
  totalAmount: number
  currency: string
}

export const InvoiceRequestModal: React.FC<InvoiceRequestModalProps> = ({
  visible,
  user,
  account,
  onClose,
  onSubmit,
  loading,
  selectedProducts,
  totalAmount,
  currency
}) => {
  const [form] = Form.useForm<InvoiceRequestFormData>()
  const { client } = useAuth()
  const [organization, setOrganization] = useState<Organization | null>(null)

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const org = await client.orgs.getOrg({ name: account.name }).then(res => res.data)
        setOrganization(org)
      } catch (error) {
        // If organization fetch fails, we'll use account data as fallback
        console.warn('Failed to fetch organization data:', error)
      }
    }

    if (visible && account.name) {
      fetchOrganization()
    }
  }, [account.name, client.orgs, visible])

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields()
      await onSubmit(values, organization)
      form.resetFields()
      onClose()
    } catch (error) {
      // Form validation errors will be shown automatically
    }
  }, [form, onSubmit, organization, onClose])

  const handleCancel = useCallback(() => {
    form.resetFields()
    onClose()
  }, [form, onClose])

  return (
    <Modal
      title="Request Invoice"
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
        >
          Submit Invoice Request
        </Button>
      ]}
      width={600}
      destroyOnClose
    >
      {/* Selected Products Summary */}
      <div style={{ marginBottom: '24px' }}>
        <Typography.Title level={5}>Selected Products:</Typography.Title>
        {selectedProducts.map((item, index) => (
          <div key={index} style={{ marginBottom: '8px' }}>
            <Typography.Text strong>{item.product.name}</Typography.Text>
            <Typography.Text type="secondary"> - {item.price.name} × {item.quantity}</Typography.Text>
          </div>
        ))}
        <Divider />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography.Text strong>Total:</Typography.Text>
          <Typography.Text strong style={{ color: '#52c41a' }}>
            {formatCurrency(totalAmount, currency)} + VAT/Tax
          </Typography.Text>
        </div>
      </div>

      {/* Pre-filled Information Display */}
      <div style={{ marginBottom: '24px' }}>
        <Typography.Title level={5} style={{ marginBottom: '16px' }}>
          Business Information
        </Typography.Title>

        <Row gutter={16}>
          <Col span={12}>
            <div style={{ marginBottom: '16px' }}>
              <Typography.Text strong style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                Business Name
              </Typography.Text>
              <div style={{
                padding: '8px 11px',
                backgroundColor: '#f5f5f5',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                color: '#000000d9',
                fontSize: '14px'
              }}>
                {organization?.name || account.display_name}
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: '16px' }}>
              <Typography.Text strong style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                Business Contact Email
              </Typography.Text>
              <div style={{
                padding: '8px 11px',
                backgroundColor: '#f5f5f5',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                color: '#000000d9',
                fontSize: '14px'
              }}>
                {organization?.contact_email || user?.email || 'No email available'}
              </div>
            </div>
          </Col>
        </Row>

        <div style={{
          padding: '8px 12px',
          backgroundColor: '#fff7e6',
          border: '1px solid #ffd591',
          borderRadius: '4px',
          marginBottom: '16px'
        }}>
          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
            💡 This information is automatically populated from your account and will be used for the invoice.
          </Typography.Text>
        </div>
      </div>

      {/* Form for Additional Information */}
      <Form
        form={form}
        layout="vertical"
        validateTrigger="onChange"
      >
        <Typography.Title level={5} style={{ marginBottom: '16px' }}>
          Additional Information Required
        </Typography.Title>

        <Form.Item
          name="additional_contact_email"
          label="Additional Contact Email (Optional)"
          rules={[
            { required: false },
            { type: 'email', message: 'Please enter a valid email' }
          ]}
        >
          <Input placeholder="Additional email to receive the invoice request confirmation" />
        </Form.Item>

        <Form.Item
          name="billing_address"
          label="Billing Address"
          rules={[{ required: true, message: 'Please enter billing address' }]}
        >
          <Input placeholder="Street address" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="city"
              label="City"
              rules={[{ required: true, message: 'Please enter city' }]}
            >
              <Input placeholder="City" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="state"
              label="State/Province"
              rules={[{ required: true, message: 'Please enter state' }]}
            >
              <Input placeholder="State" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="postal_code"
              label="Postal Code"
              rules={[{ required: true, message: 'Please enter postal code' }]}
            >
              <Input placeholder="12345" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="country"
          label="Country"
          rules={[{ required: true, message: 'Please enter country' }]}
        >
          <Select
            placeholder="Select country"
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={Object.values(CountryChoice).map(country => ({
              label: country,
              value: country
            }))}
          />
        </Form.Item>
      </Form>

      {/* Footer Information */}
      <div style={{
        marginTop: '16px',
        padding: '12px',
        backgroundColor: '#f6ffed',
        border: '1px solid #b7eb8f',
        borderRadius: '6px'
      }}>
        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
          📧 After submitting this request, you will receive a confirmation email.
          A member of our staff will reach out to you within a few business days with the next steps to facilitate payment.
        </Typography.Text>
      </div>
    </Modal>
  )
}
